import React, {
  useRef,
  useEffect,
  useCallback,
  useState,
  useMemo,
  forwardRef,
  useImperativeHandle,
  ReactNode,
  useLayoutEffect,
  Children
} from "react";
import cx from "components/src/utils/cx";
import debounce from "lodash-es/debounce";
import clamp from "lodash-es/clamp";
// import { useIntersectionObserver } from "shared/src/hooks/useIntersectionObserver";

export type MarketSimpleCarouselOnActiveItemType = (args: {
  index: number;
  isBeginning?: boolean;
  isEnd?: boolean;
}) => void;
interface MarketSimpleCarouselProps {
  children: ReactNode;
  onActiveItem?: MarketSimpleCarouselOnActiveItemType;
  initialIndex?: number;
  scrollSnap?: "center" | "start" | "end";
  hasScrolling?: boolean;
  isDesktop?: boolean;
  showNav?: boolean;
  style?: React.CSSProperties;
}

interface MarketSimpleCarouselRef {
  goToNext: () => void;
  goToPrev: () => void;
  // goToIndex: (index: number) => void;
  // getCurrentIndex: () => number;
}

const scrollSnapMap = {
  center: "cui-snap-center",
  start: "cui-snap-start",
  end: "cui-snap-end",
};

function calculateVirtualSlideIndexes(num) {
  const obj: { [key: PropertyKey]: number | string } = {};
  for (let i = 0; i <= num; i++) {
    obj[i] = (56 + 4) * i;
  }
  return obj;
}

function getCurrentIndex(ref, slideIndexes) {
  if (!ref) return 0;
  const currentScrollLeft = ref.scrollLeft;

  // Find the closest slide index based on scroll position
  const closestIndex = Object.entries(slideIndexes).reduce(
    (closest, [index, position]) => {
      const currentDistance = Math.abs(
        currentScrollLeft - (position as number)
      );
      const closestDistance = Math.abs(
        currentScrollLeft - (slideIndexes[closest] as number)
      );
      return currentDistance < closestDistance ? Number(index) : closest;
    },
    0
  );

  return closestIndex;
}

function getCenterIndex(index, offset = 2, max) {
  return clamp(index + offset, max);
}

// function calcSlidesAndOffset(container) {
//   if (!container) return { maxSlides: 0, centerOffset: 0 };
//   const maxSlides = Math.floor(container?.clientWidth / (56 + 4));
//   const centerOffset = maxSlides - Math.round(maxSlides / 2);
//   return { maxSlides, centerOffset };
// }

export const MarketSimpleCarousel = forwardRef<
  MarketSimpleCarouselRef,
  MarketSimpleCarouselProps
>(
  (
    {
      children,
      onActiveItem,
      initialIndex = 0,
      scrollSnap = "center",
      hasScrolling,
      isDesktop,
      showNav,
      style
    },
    ref
  ) => {
    const isFirstRender = useRef(true);
    const containerRef = useRef<HTMLDivElement | null>(null);
    const [activeItem, setActiveItem] = useState(initialIndex);

    const { slideIndexes, slidesByRange } = useMemo(() => {
      if (!Array.isArray(children)) return [];
      const _indexes = calculateVirtualSlideIndexes(children?.flat().length);
      return {
        slideIndexes: _indexes,
        slidesByRange: Object.entries(_indexes).map(([key, val]) => {
          return [val, key];
        }),
      } as any;
    }, [children]);

    const mappedChildren = useMemo(() => {
      return Children.map(children, (child, i) => {
        if (!child) return child;
        return (
          <div
            className={`${scrollSnapMap[scrollSnap]} ${isDesktop ? "last:cui-pr-2px" : ""}`}
            key={`${i}`}
          >
            {child}
          </div>
        );
      });
    }, [children]);

    const maxLength = mappedChildren?.length ?? 0;
    const centerOffset = isDesktop ? 2 : 1;

    /**
     * Callbacks
     */

    const handleActiveItem = useCallback(
      debounce((e) => {
        if (isFirstRender.current) {
          isFirstRender.current = false;
          return;
        }
        const container = containerRef.current;
        if (!container || !hasScrolling) return;
        const currIndex = getCurrentIndex(container, slideIndexes);

        setActiveItem(currIndex);
      }, 100),
      [scrollSnap]
    );

    const handleActiveItemTransition = useCallback(() => {
      const container = containerRef.current;
      if (!container || !hasScrolling) return;

      const indexOffset = clamp(initialIndex - centerOffset, 0, maxLength);

      setActiveItem(indexOffset);
    }, []);

    // Initial scroll position
    useLayoutEffect(handleActiveItemTransition, []);

    // ActiveItem manual change
    // useLayoutEffect(handleActiveItemTransition, []);

    // Passive scroll event listener.  Remove if not interescting.
    useEffect(() => {
      const container = containerRef.current;
      if (!container || !hasScrolling) return;

      container.addEventListener("scroll", handleActiveItem, { passive: true });

      return () => {
        container.removeEventListener("scroll", handleActiveItem);
      };
    }, [hasScrolling]);

    useEffect(() => {
      const container = containerRef.current;
      if (!container || !hasScrolling) return;

      
      const centerIndex = getCenterIndex(activeItem, centerOffset, maxLength);
      const isEnd = centerIndex >= maxLength - centerOffset - 1;
      const isBeginning = activeItem === 0;
      const offsetIndex = clamp(activeItem, 0, maxLength - centerOffset - 1)
      container.scrollTo({
        left: slideIndexes[offsetIndex],
        behavior: isFirstRender.current ? "instant" : "smooth"
      });

      // Call onActiveItem
      typeof onActiveItem === "function" &&
        onActiveItem({
          index: centerIndex,
          isBeginning,
          // isBeginning: container.scrollLeft === 0,
          isEnd,
        }); 
    }, [activeItem, centerOffset]);


    /**
     * Callbacks
     */

    // const goToIndex = (index) => {
    //   const container = containerRef.current;
    //   if (!container || !hasScrolling) return;
    // };

    const goToNext = useCallback(() => {
      setActiveItem((v) => v + 1);
    }, []);
    const goToPrev = useCallback(() => {
      setActiveItem((v) => v - 1);
    },[]);

    useImperativeHandle(ref, () => ({
      goToNext,
      goToPrev
    }));

    // useIntersectionObserver({
    //   refs: [containerRef],
    //   onVisible: () => {
    //     console.log("visible")
    //     containerRef.current?.addEventListener("scroll", handleActiveItem, { passive: true });
    //   },
    //   onInvisible: () => {
    //     console.log("invvisible")
    //     containerRef.current?.removeEventListener("scroll", handleActiveItem);
    //   }
    // });

    return (
      <div
        ref={containerRef}
        className={cx(
          {},
          "cui-overflow-x-auto cui-w-full cui-flex cui-items-center cui-justify-start cui-gap-2xs cui-snap-x cui-snap-mandatory cui-snap-always cui-no-scrollbar"
        )}
        style={style ?? {}}
      >
        {mappedChildren}
      </div>
    );
  }
);

MarketSimpleCarousel.displayName = "MarketSimpleCarousel";
